import { SplitPanelFrame } from "@/components/layout/SplitPanel/SplitPanelFrame.tsx";
import {
  PanelBreadcrumb,
  panelHeaderControlClass,
  panelHeaderCrumbTitleClass,
  panelHeaderTitleClass,
  SplitPanelBackButton,
  SplitPanelHeader,
} from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { HeaderUtilitySlot } from "@/components/layout/HeaderUtilitySlot.tsx";
import { cn } from "@/lib/utils.ts";
import { createContext, useContext, useState, type ReactNode } from "react";

/**
 * The header slot a drilled-in child screen puts its own actions in.
 *
 * The same shape `SettingsHeaderSlotContext` uses for the settings chrome: the
 * host publishes a DOM node, the child portals into it, and a child rendered
 * without a host falls back to placing its actions itself. A render prop would
 * have meant lifting each child's action state — Run's pending flag, a delete
 * dialog's open state — out of the child that owns it.
 */
const CanvasHeaderActionsContext = createContext<HTMLElement | null>(null);

/**
 * The node a child screen's actions should portal into, or `null` when there is
 * no canvas host — in which case the child places them itself, as it does on its
 * own settings page.
 */
export const useCanvasHeaderActionsSlot = () => useContext(CanvasHeaderActionsContext);

/**
 * The canvas column when it is showing a pivot rather than the calendar: one
 * view filling the whole right side.
 *
 * Its header is deliberately thin — a title and whatever global chrome the page
 * hands it. The calendar's header carries date and view controls because the
 * calendar needs them; a pivot's own controls belong to the list rather than to
 * the column, so they go in {@link toolbar}, above the thing they change and
 * inside the same measure as it.
 *
 * Uses the same {@link SplitPanelFrame} and {@link SplitPanelHeader} the
 * calendar panel does, so a pivot switch cannot change the column's geometry —
 * the header keeps its height and gutter, and only the body swaps.
 */
export const CanvasPivotPanel = ({
  title,
  parentLabel,
  onBack,
  backStatsigName,
  leading,
  accountSwitcher,
  toolbar,
  children,
}: {
  title: string;
  /**
   * Set together with `onBack` while a drilled-in child is showing: the list's
   * name, rendered as the breadcrumb back to it. Same treatment the settings
   * panels use, so drilling into a contact reads the same on either surface.
   */
  parentLabel?: string;
  onBack?: () => void;
  backStatsigName?: string;
  /** Page chrome that rides the header's leading edge, as on the calendar panel. */
  leading?: ReactNode;
  accountSwitcher?: ReactNode;
  /**
   * The page's own action row — promoted filters, search, and its primary
   * action — rendered in the content column above the list rather than in the
   * header's corner. See {@link PanelToolbar}.
   */
  toolbar?: ReactNode;
  children: ReactNode;
}) => {
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  const isMobile = useIsMobile();
  // Both are set together by a host showing a child screen; narrowing once here
  // keeps the header from re-deriving it three ways.
  const drilledIn = Boolean(parentLabel && onBack);
  // Chosen here rather than in the JSX: the two conditions nest, and a nested
  // ternary inside markup is the one shape this codebase rules out. A phone gets
  // the back chevron a stack expects; a wider screen gets the breadcrumb, which
  // can afford to name what it returns to.
  let backControl: ReactNode = null;
  if (parentLabel && onBack) {
    backControl = isMobile ? (
      <SplitPanelBackButton
        onBack={() => {
          onBack();
          return false;
        }}
        tooltip={`Back to ${parentLabel}`}
        className={panelHeaderControlClass}
      />
    ) : (
      <PanelBreadcrumb parentLabel={parentLabel} onBack={onBack} statsigName={backStatsigName} />
    );
  }

  return (
    <SplitPanelFrame
      header={
        <SplitPanelHeader>
          {leading}
          {/* Drilling in reads differently on each surface. Desktop has room
              for the trail, and the parent's name in front of the child is the
              way back. A phone has one column and a stack: the chevron is the
              way back, and a trail would spend a third of the row restating a
              screen that is no longer on it.

              The title is the same either way: a screen reached by drilling in
              is a second level whichever control announces it, so it takes the
              crumb's weight rather than the display face a top-level page
              wears. */}
          {backControl}
          <h1 className={drilledIn ? panelHeaderCrumbTitleClass : panelHeaderTitleClass}>
            {title}
          </h1>
          {/* `shrink-0`, never `flex-1`: a basis of zero let the cluster be
              squeezed to nothing on a phone, where its buttons then overflowed
              leftwards across the title instead of the title truncating in
              front of them. Sized to its content, it holds the right edge and
              the title gives way first. */}
          <div className="ml-auto flex shrink-0 items-center justify-end gap-1">
            {/* Where a drilled-in child portals its own actions, so they sit in
                the header rather than stranded at the top of the body.
                `empty:hidden` because an empty flex item still takes the
                cluster's gap, which would hold the account chip off the gutter
                its content lines up on. */}
            <div ref={setActionsEl} className="flex shrink-0 items-center gap-1 empty:hidden" />
            {/* Only at the top level. Settings and the account chip are the
                app's own chrome, and they belong on the screens a session sits
                on — the lists and the calendar — not on a routine opened from
                one, where the row is already carrying that screen's own
                actions and the way back. */}
            {!drilledIn && accountSwitcher}
            {/* Ends the row, after the account chip: the chip is the widest
                thing here on a phone, and a button in front of it reads as
                sitting on top of it rather than beside it. The chip is
                mobile-only, so on a desktop this is simply last. */}
            <HeaderUtilitySlot />
          </div>
        </SplitPanelHeader>
      }
    >
      {/* `min-h-0` is load-bearing on a flex child: its default
          `min-height:auto` refuses to shrink below its content, so the view
          would push past the column instead of scrolling inside it.
          `pb-28` clears the floating bar below, so the last row of a list can
          still be scrolled out from under it.

          `pt-2` on a phone only. There the header is the page's own title in a
          display face, and its first line of content sat straight underneath
          with nothing between them; on desktop the header is one column's label
          among several and the tighter join is right. */}
      {/* `scrollbar-gutter:stable` reserves the bar's width whether or not the
          list is long enough to need one. Without it, narrowing a list past the
          point where it stops scrolling takes the scrollbar away, the column
          grows by its width, and the centred content shifts by half of that — so
          the toolbar slid sideways under the pointer that had just clicked a
          filter, and the cards under it moved too. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-(--panel-gutter) pb-28 [scrollbar-gutter:stable]">
        {/* 960px centred — the width these same lists get on Settings, where the
            side rail takes the rest of the tier. Borrowing that tier here, with
            no rail to pay for, would buy the grid a fourth column instead.

            A cap at all because the grid is `auto-fill` on an 18rem floor: left
            alone, a wide canvas puts six cards on a line and reads as a wall.
            The container is capped rather than the scroller, so the scrollbar
            stays on the panel edge. */}
        {/* The head room sits here rather than on the scroller, because the
            toolbar below sticks to the top of it. A scroll container's own
            padding is not clipped: content scrolls up through it and paints in
            the band above a stuck child, which put a sliver of the first card
            over the filter row. Held by a box that scrolls, it is spacing at
            rest and gone once the row takes the edge. */}
        <div className="flex w-full flex-col pt-2 md:pt-0 lg:mx-auto lg:max-w-240">
          {/* Carries the page's description under itself, shown only while the
              list is unfiltered — which is why that sentence is the toolbar's
              to place rather than this panel's. */}
          {toolbar}
          <CanvasHeaderActionsContext.Provider value={actionsEl}>
            {children}
          </CanvasHeaderActionsContext.Provider>
        </div>
      </div>
      {/* The same new-chat bar the calendar floats, over the same purple glow,
          so starting a chat looks and sits identically whichever view the canvas
          is showing.

          The gradient is the calendar's, restated rather than shared: its own
          copy is welded to FullCalendar's scroller (`.calendar-create-surface
          .fc-wrapper .fc-scroller-liquid-absolute::after`), where it doubles as
          over-scroll spacing and can only exist inside that component. The stops
          are the same three — 30% purple into the popover at the floor, ~20% of
          the pink mix at the glow line just above the pill, then transparent —
          and `--fc-shim-pink`'s definition is inlined since that variable is
          declared on the calendar surface only. Heights match too (280px, 200px
          from lg), so the two read as one treatment. Keep them in step.

          Not on a phone's drilled-in screen. The glow exists to lift the bar
          that floats over this surface off the content scrolling under it, and
          that bar stands down for a second-level screen — leaving a purple wash
          across the foot of a page with nothing there to separate. */}
      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 z-30 flex h-[280px] items-end justify-center pb-8 lg:h-[200px]",
          isMobile && drilledIn && "hidden",
        )}
        style={{
          backgroundImage:
            "linear-gradient(to top," +
            " color-mix(in srgb, var(--color-purple-400, #c084fc) 30%, var(--popover)) 0," +
            " color-mix(in srgb, color-mix(in srgb, var(--color-purple-400, #c084fc) 20%, var(--popover)) 20%, transparent) 6.125rem," +
            " transparent 100%)",
        }}
      ></div>
    </SplitPanelFrame>
  );
};
